// src/belediye/components/steps/ApplicationSmsPanel.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  loadSmsTemplates,
  addSmsTemplate,
  removeSmsTemplate,
  loadApplicationSms,
  sendApplicationSms,
  selectSmsTemplates,
  selectSmsTemplatesLoading,
  selectSmsTemplatesError,
  selectSmsMessagesForApplication,
  selectSmsMessagesLoading,
  selectSmsMessagesError,
  selectSmsSending,
  selectSmsSendError,
} from "../../redux/slices/belediyeApplicationSmsSlice";

import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
  BuildingOffice2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

import "./ApplicationSmsPanel.css";

/**
 * ApplicationSmsPanel (Slice-based, redesigned)
 *
 * Props:
 * - applicationId
 * - applicationData
 * - onClose
 * - inlineMode?: boolean
 *   - true  => parent overlay/backdrop is handled by ActionPanel
 *   - false => full-screen overlay (legacy / standalone)
 */
export default function ApplicationSmsPanel({
  applicationId,
  applicationData,
  onClose,
  inlineMode = false,
}) {
  const dispatch = useDispatch();
  const listEndRef = useRef(null);

  // ---------------------------
  // Slice state
  // ---------------------------
  const templates = useSelector(selectSmsTemplates);
  const templatesLoading = useSelector(selectSmsTemplatesLoading);
  const templatesError = useSelector(selectSmsTemplatesError);

  const messages = useSelector((s) => selectSmsMessagesForApplication(s, applicationId));
  const messagesLoading = useSelector((s) => selectSmsMessagesLoading(s, applicationId));
  const messagesError = useSelector((s) => selectSmsMessagesError(s, applicationId));

  const sending = useSelector(selectSmsSending);
  const sendError = useSelector(selectSmsSendError);

  // ---------------------------
  // Application meta
  // ---------------------------
  const statusRaw = applicationData?.status || "";
  const isFinalized = statusRaw === "çözüldü" || statusRaw === "çözülemedi";

  // İstek: sonuçlandıysa panel açılsın ama sadece geçmiş görüntülensin
  const canSendSms = statusRaw === "işleme_alındı"; // sadece incelemede SMS gönderimi

  //   Unit context (çok birimli kullanıcı + template birim kaydı + send permission için)
  // applicationData.unit bazen object, bazen id, bazen unit_id gelebilir.
  const unitId = useMemo(() => {
    const raw =
      applicationData?.unit_id ??
      applicationData?.unitId ??
      applicationData?.unit?.id ??
      applicationData?.unit;

    if (raw == null) return null;

    const n = typeof raw === "number" ? raw : Number(String(raw).trim());
    return Number.isFinite(n) ? n : null;
  }, [applicationData]);

  const statusLabel = useMemo(() => {
    const s = String(statusRaw || "").trim();
    if (s === "işleme_alındı") return "İncelemede";
    if (s === "çözüldü") return "ÇÖZÜLDÜ";
    if (s === "çözülemedi") return "ÇÖZÜLEMEDİ";
    if (s === "yeni") return "YENİ";
    return s ? s.toUpperCase() : "";
  }, [statusRaw]);

  const headerMeta = useMemo(() => {
    const tracking =
      applicationData?.tracking_number ||
      applicationData?.trackingNumber ||
      applicationData?.tracking ||
      "";

    // Birim adı: unit_name veya unit.name tercih
    const unitObj = applicationData?.unit;
    const unitName =
      applicationData?.unit_name ||
      unitObj?.name ||
      (typeof unitObj === "string" && unitObj.trim() !== "" ? unitObj : "");

    // Unit sadece ID gibi geliyorsa göstermeyelim
    const unitNameSafe = /^\d+$/.test(String(unitName || "")) ? "" : String(unitName || "");

    return { tracking, unitName: unitNameSafe };
  }, [applicationData]);

  // ---------------------------
  // UI state
  // ---------------------------
  const [draft, setDraft] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  // Accordion: templates area
  const [tplOpen, setTplOpen] = useState(false);
  const [tplSearch, setTplSearch] = useState("");

  // Create template modal
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [templateTitle, setTemplateTitle] = useState("");
  const [templateBody, setTemplateBody] = useState("");
  const [templateLocalError, setTemplateLocalError] = useState("");

  // Delete confirm
  const [deleteTplId, setDeleteTplId] = useState(null);

  // Send confirmation flow
  const [isConfirmSendOpen, setIsConfirmSendOpen] = useState(false);
  const [isSaveAsTplPromptOpen, setIsSaveAsTplPromptOpen] = useState(false);
  const [saveAsTplWanted, setSaveAsTplWanted] = useState(false);
  const [saveAsTplTitle, setSaveAsTplTitle] = useState("");

  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    return templates.find((t) => String(t.id) === String(selectedTemplateId)) || null;
  }, [selectedTemplateId, templates]);

  const filteredTemplates = useMemo(() => {
    const q = (tplSearch || "").trim().toLowerCase();
    if (!q) return templates;

    return templates.filter((t) => {
      const title = String(t?.title || "").toLowerCase();
      const text = String(t?.template_text || t?.body || t?.text || "").toLowerCase();
      return title.includes(q) || text.includes(q);
    });
  }, [templates, tplSearch]);

  // ---------------------------
  // Helpers
  // ---------------------------
  const scrollToBottom = useCallback(() => {
    try {
      listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    } catch {
      // ignore
    }
  }, []);

  const formatDateTime = useCallback((iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return String(iso);
      return d.toLocaleString("tr-TR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(iso);
    }
  }, []);

  const getDisplayName = useCallback((u) => {
    if (!u) return "";
    const first = (u.first_name || u.firstName || "").trim();
    const last = (u.last_name || u.lastName || "").trim();
    const full = `${first} ${last}`.trim();
    if (full) return full;
    return (u.username || u.email || "").trim();
  }, []);

  // Otomatik mesajlar “Sistem”
  const resolveSenderLabel = useCallback(
    (msg) => {
      const createdBy = msg?.created_by || msg?.createdBy || null;
      const createdById = createdBy?.id ?? msg?.created_by_id ?? msg?.created_by ?? null;

      const type = String(msg?.type || msg?.notification_type || msg?.purpose || "").toLowerCase();
      const isSystemTyped =
        type.includes("system") ||
        type.includes("otomatik") ||
        type.includes("auto") ||
        type.includes("durum") ||
        type.includes("status");

      if (!createdBy && (createdById === null || createdById === undefined)) return "Sistem";
      if (isSystemTyped) return "Sistem";

      const label =
        msg?.created_by_display ||
        msg?.created_by_name ||
        createdBy?.full_name ||
        getDisplayName(createdBy);

      return label || "Personel";
    },
    [getDisplayName]
  );

  const normalizeMessageText = useCallback((msg) => {
    return msg?.message || msg?.text || msg?.body || msg?.content || msg?.sms_text || "";
  }, []);

  const snippet = useCallback((text, n = 160) => {
    const t = String(text || "").replace(/\s+/g, " ").trim();
    if (!t) return "";
    if (t.length <= n) return t;
    return t.slice(0, n - 1) + "…";
  }, []);

  const safeId = useCallback((v) => {
    if (typeof v === "number") return v;
    const s = String(v || "").trim();
    if (!s) return null;
    const n = Number(s);
    return Number.isNaN(n) ? null : n;
  }, []);

  // ---------------------------
  // Data load
  // ---------------------------
  useEffect(() => {
    //   templates: mümkünse unit bazlı çek (tasarımı bozmaz, sadece sonuçlar doğru olur)
    dispatch(loadSmsTemplates(unitId ? { unit_id: unitId } : undefined));

    // messages: thunk şu an sadece applicationId alıyor; signature'ı bozmuyoruz
    dispatch(loadApplicationSms(applicationId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, unitId]);

  useEffect(() => {
    requestAnimationFrame(scrollToBottom);
  }, [messages?.length, scrollToBottom]);

  // ESC to close modals/panels inside
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;

      setIsConfirmSendOpen(false);
      setIsSaveAsTplPromptOpen(false);
      setIsTemplateModalOpen(false);
      setDeleteTplId(null);
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, []);

  // ---------------------------
  // Actions
  // ---------------------------
  const handleRefresh = useCallback(() => {
    dispatch(loadApplicationSms(applicationId));
    dispatch(loadSmsTemplates(unitId ? { unit_id: unitId } : undefined));
  }, [dispatch, applicationId, unitId]);

  const clearTemplateSelection = useCallback(() => {
    setSelectedTemplateId("");
  }, []);

  const applyTemplate = useCallback(
    (tpl) => {
      if (!tpl) return;
      const body = tpl?.template_text ?? tpl?.body ?? tpl?.text ?? "";
      setSelectedTemplateId(String(tpl.id));
      setDraft(String(body || ""));
      requestAnimationFrame(scrollToBottom);
    },
    [scrollToBottom]
  );

  const openTemplateModal = useCallback(() => {
    setTemplateLocalError("");
    setIsTemplateModalOpen(true);
    setTemplateTitle("");
    setTemplateBody((draft || "").trim() ? draft : "");
  }, [draft]);

  const closeTemplateModal = useCallback(() => {
    setIsTemplateModalOpen(false);
    setTemplateTitle("");
    setTemplateBody("");
    setTemplateLocalError("");
  }, []);

  const saveTemplate = useCallback(async () => {
    setTemplateLocalError("");
    const title = (templateTitle || "").trim();
    const body = (templateBody || "").trim();

    if (!title) {
      setTemplateLocalError("Şablon adı zorunlu.");
      return;
    }
    if (!body) {
      setTemplateLocalError("Şablon metni zorunlu.");
      return;
    }

    //   birime de kaydet: unit_id ekle (varsa)
    const payload = {
      title,
      template_text: body,
      ...(unitId ? { unit_id: unitId } : {}),
    };

    const res = await dispatch(addSmsTemplate(payload));
    if (addSmsTemplate.fulfilled.match(res)) {
      closeTemplateModal();
      setTplOpen(true);
      dispatch(loadSmsTemplates(unitId ? { unit_id: unitId } : undefined));
    } else {
      setTemplateLocalError(String(res.payload || "Şablon kaydedilemedi."));
    }
  }, [dispatch, templateTitle, templateBody, closeTemplateModal, unitId]);

  const requestDeleteTemplate = useCallback((tplId) => {
    setDeleteTplId(tplId || null);
  }, []);

  const cancelDeleteTemplate = useCallback(() => setDeleteTplId(null), []);

  const confirmDeleteTemplate = useCallback(async () => {
    const tplId = deleteTplId;
    if (!tplId) return;

    const res = await dispatch(removeSmsTemplate(tplId));
    setDeleteTplId(null);

    if (removeSmsTemplate.fulfilled.match(res)) {
      if (String(selectedTemplateId) === String(tplId)) setSelectedTemplateId("");
      dispatch(loadSmsTemplates(unitId ? { unit_id: unitId } : undefined));
    }
  }, [dispatch, deleteTplId, selectedTemplateId, unitId]);

  const openSendFlow = useCallback(() => {
    const msg = (draft || "").trim();
    if (!msg) return;
    setIsConfirmSendOpen(true);
  }, [draft]);

  const confirmSendNo = useCallback(() => setIsConfirmSendOpen(false), []);

  const resetSaveAsTemplateState = useCallback(() => {
    setSaveAsTplWanted(false);
    setSaveAsTplTitle("");
  }, []);

  const doSend = useCallback(
    async ({ save_as_template, template_title }) => {
      const msg = (draft || "").trim();
      if (!msg) return;

      //   unit_id ekle (varsa): multi-unit 403 + doğru permission için kritik
      const payload = {
        application_id: safeId(applicationId) ?? Number(applicationId),
        text: msg,
        ...(selectedTemplateId ? { template_id: safeId(selectedTemplateId) ?? Number(selectedTemplateId) } : {}),
        ...(save_as_template ? { save_as_template: true, template_title } : {}),
        ...(unitId ? { unit_id: unitId } : {}),
      };

      const res = await dispatch(sendApplicationSms(payload));

      if (sendApplicationSms.fulfilled.match(res)) {
        setDraft("");
        setSelectedTemplateId("");
        setIsSaveAsTplPromptOpen(false);
        setIsConfirmSendOpen(false);
        resetSaveAsTemplateState();

        // listeyi yenile
        dispatch(loadApplicationSms(applicationId));
        dispatch(loadSmsTemplates(unitId ? { unit_id: unitId } : undefined));
      } else {
        // hata durumunda metin kalsın; sadece modal kapat
        setIsSaveAsTplPromptOpen(false);
        setIsConfirmSendOpen(false);
      }
    },
    [dispatch, draft, applicationId, selectedTemplateId, resetSaveAsTemplateState, safeId, unitId]
  );

  const confirmSendYes = useCallback(() => {
    setIsConfirmSendOpen(false);

    if (!canSendSms) return;

    // template seçilmediyse: kaydetme sorusu
    if (!selectedTemplateId) {
      resetSaveAsTemplateState();
      setIsSaveAsTplPromptOpen(true);
      return;
    }

    doSend({ save_as_template: false });
  }, [canSendSms, selectedTemplateId, doSend, resetSaveAsTemplateState]);

  const proceedSaveAsTplPrompt = useCallback(async () => {
    if (!saveAsTplWanted) {
      await doSend({ save_as_template: false });
      return;
    }

    const title = (saveAsTplTitle || "").trim();
    if (!title) return;

    await doSend({ save_as_template: true, template_title: title });
  }, [saveAsTplWanted, saveAsTplTitle, doSend]);

  const cancelSaveAsTplPrompt = useCallback(() => {
    setIsSaveAsTplPromptOpen(false);
    resetSaveAsTemplateState();
  }, [resetSaveAsTemplateState]);

  // ---------------------------
  // Render
  // ---------------------------
  const PanelShell = inlineMode ? InlineShell : OverlayShell;

  return (
    <PanelShell onClose={onClose}>
      <div className="asp-sheet" role="dialog" aria-modal="true" aria-label="Vatandaşa SMS">
        {/* Header */}
        <div className="asp-header">
          <div className="asp-header-left">
            <div className="asp-title">
              <ChatBubbleLeftRightIcon className="asp-title-icon" />
              <span>Vatandaşa SMS</span>
            </div>

            <div className="asp-subtitle">
              {headerMeta.tracking ? (
                <span className="asp-chip">
                  Takip No: <b>{headerMeta.tracking}</b>
                </span>
              ) : null}

              {headerMeta.unitName ? (
                <span className="asp-chip">
                  <BuildingOffice2Icon className="asp-chip-ico" />
                  <b>{headerMeta.unitName}</b>
                </span>
              ) : null}

              {statusLabel ? (
                <span className="asp-chip asp-chip--status">
                  <ClipboardDocumentListIcon className="asp-chip-ico" />
                  <b>{statusLabel}</b>
                </span>
              ) : null}
            </div>
          </div>

          <div className="asp-header-right">
            <button
              type="button"
              className="asp-icon-btn"
              onClick={handleRefresh}
              disabled={messagesLoading}
              title="Yenile"
            >
              <ArrowPathIcon className="asp-icon" />
            </button>

            <button type="button" className="asp-icon-btn" onClick={onClose} title="Kapat">
              <XMarkIcon className="asp-icon" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="asp-body">
          {!canSendSms && (
            <div className="asp-banner asp-banner--warn">
              <ExclamationTriangleIcon className="asp-banner-ico" />
              <div>
                {isFinalized
                  ? "Başvuru sonuçlandığı için SMS gönderimi kapalıdır. Sadece geçmiş mesajları görüntüleyebilirsiniz."
                  : "SMS gönderimi yalnızca başvuru “İncelemede” durumundayken aktiftir."}
              </div>
            </div>
          )}

          {templatesError ? <div className="asp-banner asp-banner--error">{String(templatesError)}</div> : null}
          {messagesError ? <div className="asp-banner asp-banner--error">{String(messagesError)}</div> : null}
          {sendError ? <div className="asp-banner asp-banner--error">{String(sendError)}</div> : null}

          {/* Templates accordion (panel içinde, taşma yok) */}
          <div className="asp-accordion">
            <button
              type="button"
              className="asp-acc-head"
              onClick={() => setTplOpen((v) => !v)}
              disabled={templatesLoading}
            >
              <div className="asp-acc-head-left">
                <DocumentTextIcon className="asp-mini-icon" />
                <div className="asp-acc-title">Şablonlar</div>
                <div className="asp-acc-sub">
                  {selectedTemplate ? (
                    <span className="asp-pill asp-pill--selected">
                      Seçili: <b>{selectedTemplate.title}</b>
                    </span>
                  ) : (
                    <span className="asp-pill">Seçim yapabilir veya yeni şablon oluşturabilirsiniz</span>
                  )}
                </div>
              </div>

              <div className="asp-acc-head-right">
                {tplOpen ? <ChevronUpIcon className="asp-icon" /> : <ChevronDownIcon className="asp-icon" />}
              </div>
            </button>

            {tplOpen && (
              <div className="asp-acc-body">
                <div className="asp-tpl-top">
                  <div className="asp-search">
                    <MagnifyingGlassIcon className="asp-search-ico" />
                    <input
                      className="asp-search-input"
                      value={tplSearch}
                      onChange={(e) => setTplSearch(e.target.value)}
                      placeholder="Şablonlarda ara…"
                    />
                  </div>

                  <div className="asp-tpl-actions">
                    {selectedTemplateId ? (
                      <button type="button" className="asp-btn asp-btn--secondary" onClick={clearTemplateSelection}>
                        <XMarkIcon className="asp-mini-icon" />
                        Temizle
                      </button>
                    ) : null}

                    <button type="button" className="asp-btn asp-btn--secondary" onClick={openTemplateModal}>
                      <PlusIcon className="asp-mini-icon" />
                      Yeni Şablon
                    </button>

                    {selectedTemplateId ? (
                      <button
                        type="button"
                        className="asp-btn asp-btn--danger"
                        onClick={() => requestDeleteTemplate(selectedTemplateId)}
                      >
                        <TrashIcon className="asp-mini-icon" />
                        Sil
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="asp-tpl-list">
                  {templatesLoading ? (
                    <div className="asp-loading">Şablonlar yükleniyor…</div>
                  ) : filteredTemplates.length === 0 ? (
                    <div className="asp-empty">Şablon bulunamadı.</div>
                  ) : (
                    filteredTemplates.map((t) => {
                      const body = t?.template_text ?? t?.body ?? t?.text ?? "";
                      const isSel = String(selectedTemplateId) === String(t.id);

                      return (
                        <button
                          key={t.id}
                          type="button"
                          className={["asp-tpl-item", isSel ? "is-selected" : ""].join(" ")}
                          onClick={() => applyTemplate(t)}
                        >
                          <div className="asp-tpl-item-top">
                            <div className="asp-tpl-item-title">
                              <DocumentTextIcon className="asp-mini-icon" />
                              <span>{t.title}</span>
                            </div>
                            {isSel ? (
                              <span className="asp-badge">
                                <CheckCircleIcon className="asp-badge-ico" />
                                Seçili
                              </span>
                            ) : null}
                          </div>

                          <div className="asp-tpl-item-body">{snippet(body, 180)}</div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Conversation (scroll) */}
          <div className="asp-conversation">
            {messagesLoading ? (
              <div className="asp-loading">SMS geçmişi yükleniyor…</div>
            ) : !messages || messages.length === 0 ? (
              <div className="asp-empty">Bu başvuru için henüz SMS kaydı yok.</div>
            ) : (
              messages.map((m, idx) => {
                const text = normalizeMessageText(m);
                const when = m?.sent_at || m?.created_at || m?.timestamp || null;

                // İstek: tüm mesajlar sağda tek hiz
                return (
                  <div key={m?.id ?? `${idx}-${when || ""}`} className="asp-msg-row asp-msg-row--right">
                    <div className="asp-msg-meta">
                      <span className="asp-msg-sender">{resolveSenderLabel(m)}</span>
                      {when ? <span className="asp-msg-time">{formatDateTime(when)}</span> : null}
                    </div>
                    <div className="asp-msg-bubble">{text}</div>
                  </div>
                );
              })
            )}
            <div ref={listEndRef} />
          </div>

          {/* Composer (sticky bottom inside sheet) */}
          <div className="asp-composer">
            <textarea
              className="asp-textarea"
              placeholder={
                canSendSms
                  ? "Vatandaşa iletilecek SMS metnini yazın…"
                  : "Bu başvuru statüsünde SMS gönderimi kapalıdır."
              }
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={!canSendSms || sending}
            />

            <div className="asp-composer-actions">
              <div className="asp-hint">
                {selectedTemplate ? (
                  <span>
                    Seçili şablon: <b>{selectedTemplate.title}</b>
                  </span>
                ) : (
                  <span>Şablon seçmeden yazarsanız, gönderimde isterseniz şablon olarak kaydedebilirsiniz.</span>
                )}
              </div>

              <button
                type="button"
                className="asp-btn asp-btn--primary"
                onClick={openSendFlow}
                disabled={!canSendSms || sending || !(draft || "").trim()}
              >
                <PaperAirplaneIcon className="asp-mini-icon asp-mini-icon--white" />
                {sending ? "Gönderiliyor…" : "Gönder"}
              </button>
            </div>
          </div>
        </div>

        {/* Confirm send modal */}
        {isConfirmSendOpen && (
          <Modal
            title="SMS gönderilsin mi?"
            icon="warn"
            onClose={confirmSendNo}
            actions={
              <>
                <button type="button" className="asp-btn asp-btn--secondary" onClick={confirmSendNo}>
                  Vazgeç
                </button>
                <button type="button" className="asp-btn asp-btn--primary" onClick={confirmSendYes}>
                  Evet, Gönder
                </button>
              </>
            }
          >
            <div className="asp-modal-text">Aşağıdaki metin vatandaşa SMS olarak gönderilecek.</div>
            <div className="asp-modal-preview">{(draft || "").trim()}</div>
          </Modal>
        )}

        {/* Save as template prompt */}
        {isSaveAsTplPromptOpen && (
          <Modal
            title="Bu metin şablon olarak kaydedilsin mi?"
            icon="doc"
            onClose={cancelSaveAsTplPrompt}
            actions={
              <>
                <button type="button" className="asp-btn asp-btn--secondary" onClick={cancelSaveAsTplPrompt}>
                  Hayır
                </button>
                <button
                  type="button"
                  className="asp-btn asp-btn--primary"
                  onClick={proceedSaveAsTplPrompt}
                  disabled={saveAsTplWanted && !(saveAsTplTitle || "").trim()}
                >
                  Devam Et
                </button>
              </>
            }
          >
            <label className="asp-check">
              <input
                type="checkbox"
                checked={saveAsTplWanted}
                onChange={(e) => setSaveAsTplWanted(e.target.checked)}
              />
              <span>Bu metni yeni şablon olarak kaydet</span>
            </label>

            {saveAsTplWanted && (
              <label className="asp-label">
                Şablon Adı
                <input
                  className="asp-input"
                  value={saveAsTplTitle}
                  onChange={(e) => setSaveAsTplTitle(e.target.value)}
                  placeholder="Örn: Bilgilendirme"
                />
              </label>
            )}

            <div className="asp-modal-note">Not: Şablon kaydetmezseniz sadece SMS gönderilir.</div>
          </Modal>
        )}

        {/* Template create modal */}
        {isTemplateModalOpen && (
          <Modal
            title="Yeni SMS Şablonu"
            icon="doc"
            onClose={closeTemplateModal}
            actions={
              <>
                <button type="button" className="asp-btn asp-btn--secondary" onClick={closeTemplateModal}>
                  Vazgeç
                </button>
                <button type="button" className="asp-btn asp-btn--primary" onClick={saveTemplate}>
                  Kaydet
                </button>
              </>
            }
          >
            {templateLocalError ? <div className="asp-banner asp-banner--error">{templateLocalError}</div> : null}

            <label className="asp-label">
              Şablon Adı
              <input
                className="asp-input"
                value={templateTitle}
                onChange={(e) => setTemplateTitle(e.target.value)}
                placeholder="Örn: Bilgilendirme 1"
              />
            </label>

            <label className="asp-label">
              Şablon Metni
              <textarea
                className="asp-textarea asp-textarea--modal"
                value={templateBody}
                onChange={(e) => setTemplateBody(e.target.value)}
                placeholder="Şablon SMS metnini yazın…"
              />
            </label>
          </Modal>
        )}

        {/* Delete template confirm */}
        {deleteTplId && (
          <Modal
            title="Şablon silinsin mi?"
            icon="warn"
            onClose={cancelDeleteTemplate}
            actions={
              <>
                <button type="button" className="asp-btn asp-btn--secondary" onClick={cancelDeleteTemplate}>
                  Vazgeç
                </button>
                <button type="button" className="asp-btn asp-btn--danger" onClick={confirmDeleteTemplate}>
                  Sil
                </button>
              </>
            }
          >
            <div className="asp-modal-text">Seçili şablon kalıcı olarak silinecek.</div>
          </Modal>
        )}
      </div>
    </PanelShell>
  );
}

/**
 * Shells
 * - OverlayShell: full-screen overlay
 * - InlineShell: parent overlay/backdrop handled by ActionPanel
 */
function OverlayShell({ children, onClose }) {
  return (
    <div
      className="asp-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target?.classList?.contains("asp-overlay")) onClose?.();
      }}
    >
      <div className="asp-shell" onMouseDown={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function InlineShell({ children }) {
  return <div className="asp-inline">{children}</div>;
}

/**
 * Minimal modal
 */
function Modal({ title, icon = "doc", children, actions, onClose }) {
  return (
    <div
      className="asp-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target?.classList?.contains("asp-modal-backdrop")) onClose?.();
      }}
    >
      <div className="asp-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="asp-modal-header">
          <div className="asp-modal-title">
            <span className={["asp-modal-ico", icon === "warn" ? "is-warn" : "is-doc"].join(" ")} />
            {title}
          </div>
          <button type="button" className="asp-icon-btn" onClick={onClose} title="Kapat">
            <XMarkIcon className="asp-icon" />
          </button>
        </div>

        <div className="asp-modal-body">{children}</div>
        <div className="asp-modal-actions">{actions}</div>
      </div>
    </div>
  );
}
